import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../../services/auth.service';
import { ManualService, ManualPageDto } from '../../../../services/manual.service';

@Component({
  selector: 'app-manual',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './manual.component.html',
  styleUrl: './manual.component.css'
})
export class ManualComponent implements OnInit {
  pages: ManualPageDto[] = [];
  selectedPage: ManualPageDto | null = null;
  imageUrl: string = '';
  isLoading: boolean = true;
  errorMessage: string = '';

  private userRole: string = '';
  /** 画像URLに使う言語プレフィックス: 'JP' or 'EN' */
  private langPrefix: string = 'JP';

  constructor(
    private auth: AuthService,
    private translate: TranslateService,
    private manualService: ManualService
  ) {}

  ngOnInit(): void {
    this.userRole = this.auth.getRole() ?? 'Staff';
    const siteLang = localStorage.getItem('site_lang') ?? 'en';
    this.langPrefix = siteLang.toLowerCase() === 'en' ? 'EN' : 'JP';
    this.loadPages(siteLang);
  }

  private loadPages(lang: string): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.manualService.getManualPages(lang).subscribe({
      next: (pages) => {
        this.pages = pages;
        this.isLoading = false;
        if (pages.length > 0) {
          this.selectPage(pages[0]);
        }
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = this.translate.instant('MANUAL_LOAD_ERROR');
      }
    });
  }

  selectPage(page: ManualPageDto): void {
    this.selectedPage = page;
    // 命名規則: {LANG}_{Role}{PageNo}.PNG  例: JP_Staff1.PNG / EN_Rep3.PNG
    // 大文字拡張子 .PNG を優先（実ファイルに合わせる）
    this.imageUrl = `assets/manual/${this.langPrefix}_${this.userRole}${page.pageNo}.PNG`;
  }

  /**
   * 画像読み込み失敗時のフォールバック処理
   * 大文字/小文字の揺れに対応するため、以下の順で再試行する:
   *   1. JP_Role1.PNG  ← 初回生成（大文字拡張子）
   *   2. JP_Role1.png  ← 小文字拡張子
   *   3. jp_Role1.PNG  ← 小文字プレフィックス＋大文字拡張子
   *   4. jp_Role1.png  ← 全て小文字
   */
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    const step = parseInt(img.dataset['fallback'] ?? '0', 10);
    if (step >= 3) return; // 全候補試行済み
    img.dataset['fallback'] = String(step + 1);

    // 現在のURLのファイル名部分（拡張子除く）を基に次の候補を生成
    const base = `assets/manual/${this.langPrefix}_${this.userRole}${this.selectedPage?.pageNo}`;
    const candidates = [
      `${base}.PNG`,  // step 0 → 1: 大文字拡張子（初回）
      `${base}.png`,  // step 1 → 2: 小文字拡張子
      `assets/manual/${this.langPrefix.toLowerCase()}_${this.userRole}${this.selectedPage?.pageNo}.PNG`,  // step 2 → 3: 小文字prefix＋大文字拡張子
      `assets/manual/${this.langPrefix.toLowerCase()}_${this.userRole}${this.selectedPage?.pageNo}.png`,  // step 3 → 終了: 全小文字
    ];
    img.src = candidates[step + 1];
  }
}
